import { redirect } from "react-router";
import { createAuthLoader } from "@shared-ui/loaders/auth-loader";
import { tenantIdFromPathname } from "@shared-ui/utils/tenant";
import { toReturnPath } from "@shared-ui/utils/returnTo";
import { ENV } from "@/config/env";
import { path } from "@/utils/common";
import { readTenantHomeScreenDefault } from "@/utils/tenantFeatureFlag";
import { ROUTE_MYPAGE } from "@/utils/const";
import { markHomeEntry, parseHomeScreen, resolveHome } from "@/lib/home";
import { readCache } from "@/lib/preferences/storage";
// PRODUCT_DEV-748: Minizo ビルドでは "minizo" になる。clearToken(utils/logout) と
// 必ず同じ値を使うこと（片方だけ違うとログアウトでトークンが消えない）。
import { IS_MINIZO, TOKEN_STORE_NAME } from "@/config/variant";
import { apiGet } from "@shared-ui/utils/api";
import {
  API_PRESETS,
  API_DICTIONARIES,
  API_REALTIME_CONFIGS,
  API_ONP_PROMPT_SETTINGS,
  API_GLOBAL_DICTIONARY,
  API_MY_DEPARTMENT,
  AUTH_REFRESH,
} from "@/utils/const";
import type { WhoamiMe } from "@shared-domain/auth-service/contract";
import type {
  PresetsResponse,
  DictionariesResponse,
  RealtimeConfigsResponse,
  MyDepartmentResponse,
  MyDepartmentItem,
  DDBPreset,
  DDBDictionary,
  DDBRealtimeConfig,
} from "@shared-domain/unizo/contract";

/** PRODUCT_DEV-572: 自分の所属部署（未所属は null） */
export type MyDepartment = MyDepartmentResponse["department"];
import type { DDBONPPromptSetting, DDBGlobalDictionaryEntry } from "@shared-domain/admin/contract";
import type { ONPSettingsMap } from "@/lib/prompts/compose";

export interface RootLoaderData {
  /** 認証情報 */
  auth: WhoamiMe;
  /** プリセット一覧（Unizo 固有） */
  presets: DDBPreset[];
  /** 辞書マスタ（有効なもののみ） */
  dictionaries: DDBDictionary[];
  /** リアルタイムAPI設定（アクティブなもののみ） */
  realtimeConfigs: DDBRealtimeConfig[];
  /** ONPプロンプト設定（Admin管理、フォールバック用にundefined許容） */
  onpSettings?: ONPSettingsMap;
  /** グローバル辞書（Admin管理、テナント横断） */
  globalDictionary: DDBGlobalDictionaryEntry[];
  /**
   * PRODUCT_DEV-572: 自分の所属部署（未所属/取得失敗は null = 絞り込みなし）。
   *
   * ⚠ **兼務している人でも1つしか入らない。** どの部署として見ているかは
   * `useActiveDepartment()` が `myDepartments` から決める（PRODUCT_DEV-917）。
   * 新しく書く画面はそちらを使うこと。
   */
  myDepartment: MyDepartment;
  /**
   * PRODUCT_DEV-917: 所属している部署の全部。**2つ以上あるときだけ部署の切替を出す。**
   * 古いサーバは返さないので undefined になりうる（そのときは1部署の人として振る舞う）。
   */
  myDepartments?: MyDepartmentItem[];
  /**
   * PRODUCT_DEV-721: **どこか1つでも** manager をしているか（業務軸）。
   * 部の集計を見せるかどうかの出し分けに使う。**認可はサーバ側が正**（改ざんは許容）。
   * 取得失敗時は false（見せない側に倒す）。
   */
  isManager: boolean;
}

/**
 * Root loader
 * 認証情報とグローバル設定を取得
 */
export async function rootLoader({
  request,
}: {
  request: Request;
}): Promise<RootLoaderData> {
  const url = new URL(request.url);

  // エラーページ（403/404）の場合はAPI呼び出しをスキップ
  // これにより、IP制限などで403が返る場合の無限リダイレクトを防ぐ
  if (url.pathname.includes("/403") || url.pathname.includes("/404")) {
    return {
      auth: {
        user: {
          id: "",
          name: "",
          email: "",
        },
        memberships: [],
        currentTenantId: "",
        info: undefined,
      },
      presets: [],
      dictionaries: [],
      realtimeConfigs: [],
      onpSettings: undefined,
      globalDictionary: [],
      myDepartment: null,
      myDepartments: [],
      // エラーページでは所属を引いていないので、見せない側へ倒す
      isManager: false,
    };
  }

  // Phase 1: 認証処理（共通ロジック）
  const authLoader = createAuthLoader({
    apiEndpoint: ENV.PRODUCT_API_ENDPOINT,
    authPortalUrl: ENV.AUTH_PORTAL_URL,
    productId: ENV.PRODUCT_ID,
    tokenStoreName: TOKEN_STORE_NAME,
    userPoolId: ENV.COGNITO_USERPOOL_ID,
    clientId: ENV.CLIENT_ID,
    authRefreshUrl: AUTH_REFRESH,
    // PRODUCT_DEV-641: 配信ホストでテナントが決まる環境だけ非空。
    // 空文字ではなく undefined を渡す（loader 側の分岐を「未指定」で素通しさせる）。
    fixedTenantId: ENV.FIXED_TENANT_ID || undefined,
    // PRODUCT_DEV-916: ログイン後に、開こうとしていた画面（商談詳細など）へ戻す。
    restoreReturnTo: true,
    // PRODUCT_DEV-1129: セッション切れの再ログインで、テナントを選び直させない。
    rememberLastTenant: true,
  });

  const auth = await authLoader({ request });

  // PRODUCT_DEV-421: 認証済みユーザが /<tenantId>/ で来たら / にリダイレクト
  // （`/<tenantId>/` はエイリアスログイン専用入口のため、認証済なら通常 URL に戻す）
  // Unizo の React Router には `/:tenantId/*` ルートが無く、放置すると 404 にマッチするため明示的に処理する
  //
  // PRODUCT_DEV-641: 判定の正本は `@shared-ui/utils/tenant`。以前はここと
  // auth-loader に同じ正規表現が重複しており、片方だけ直すと
  // 「ポータルへは tenant 付きで飛ぶのに戻しが効かない」が静かに起きる形だった。
  //
  // PRODUCT_DEV-916: `/<tenantId>/meetings/1` のようにテナントの後ろにパスがあれば、
  // テナントだけを外してその画面へ移す（以前は `/` へ戻してパスを捨てていた）。
  // `/<tenantId>/` だけなら従来どおり `/`。外した結果が別ホストを指す値は null になる。
  if (tenantIdFromPathname(url.pathname)) {
    throw redirect(toReturnPath(url.pathname, url.search) ?? "/");
  }

  /**
   * PRODUCT_DEV-782: **アプリを開いたら手帳**にする（Minizo は手帳を持たないので送らない）。
   *
   * ⚠ **振り分けをここでやるのには理由が2つある。**
   *   1. 画面（コンポーネント）で判定すると、商談記録一覧が絞り込みを URL へ書き戻すたびに
   *      再判定が走る。全条件を既定に戻すとパラメータが 0 個になり `/` に戻るので、
   *      **一覧を使っている最中に手帳へ飛ばされる**。root-loader は遷移では再実行されない
   *      （`shouldRevalidateRoot`）ので、ここなら「開いたとき1回」になる。
   *   2. 画面で振り分けると `/` のルートが `route.lazy` から外れ、商談記録一覧が
   *      初期バンドルに丸ごと入る（PRODUCT_DEV-703 の分割が `/` だけ崩れる）。
   *
   * ⚠ **パラメータが付いた `/` は送らない。** 絞り込みを URL に載せる作りなので、
   * 共有リンクとブックマークが黙って別の画面を開くことになる。
   *
   * PRODUCT_DEV-1070: HOME に商談一覧を選んだ人は送らない（`/` は商談一覧）。
   * ⚠ 個人設定（`PreferencesProvider`）はこの時点ではまだ無いので、**端末の下書き**で決める。
   * 下書きが無い端末（初めて使う端末・消えた直後）だけは、その1回は会社の初期表示ページ（未設定ならマイ手帳）が開く。
   * 会社の初期表示ページ（テナント設定）は whoami に載っているので、端末に関係なくその場の値で決める。
   */
  if (!IS_MINIZO && url.pathname === path("/") && url.search === "") {
    // PRODUCT_DEV-1092: HOME が活動サマリのマネージャーは、着いた画面（マイ手帳・商談一覧）が
    // この印を見て送り直す（`useHomeEntryRedirect`）。役割はこの先の取得で分かるので、ここでは決められない。
    // 会社の初期表示ページが商談一覧だと手帳を通らないので、印は振り分けの前に立てる
    markHomeEntry();
    if (
      resolveHome(
        parseHomeScreen(readCache("homeScreen")),
        readTenantHomeScreenDefault(auth),
      ).screen === "diary"
    ) {
      throw redirect(ROUTE_MYPAGE);
    }
  }

  // Phase 2: グローバル設定取得（Unizo 固有）
  // TODO: 認証成功後に tenantId を取得できるようにする
  // 現在は暫定で最初の membership の tenantId を使用
  const tenantId = auth.currentTenantId || auth.memberships[0]?.tenantId;

  // 並列取得 (404を許容するためallでなくallSettledを使用する)
  //
  // PRODUCT_DEV-652: companies / users はここから外し MasterDataProvider へ移した。
  // loader はナビゲーションをブロックするため、ここに残っている fetch の**最遅の 1 本**が
  // そのまま初回描画までの待ち時間になる。テナント規模に比例して伸びるのはこの 2 本だけで、
  // 残りはテナントによらずほぼ一定サイズ。詳細は contexts/MasterDataContext.tsx を参照。
  const [presetsResponse, dictionariesResponse, realtimeConfigsResponse, onpSettingsResponse, globalDictionaryResponse, myDepartmentResponse] =
    await Promise.allSettled([
      fetchPresets(tenantId),
      fetchDictionaries(tenantId),
      fetchRealtimeConfigs(tenantId),
      fetchONPSettings(tenantId),
      fetchGlobalDictionary(tenantId),
      fetchMyDepartment(tenantId),
    ]);

  return {
    auth,
    presets:
      presetsResponse.status === "fulfilled" ? presetsResponse.value : [],
    dictionaries:
      dictionariesResponse.status === "fulfilled" ? dictionariesResponse.value : [],
    realtimeConfigs:
      realtimeConfigsResponse.status === "fulfilled" ? realtimeConfigsResponse.value : [],
    onpSettings:
      onpSettingsResponse.status === "fulfilled" ? onpSettingsResponse.value : undefined,
    globalDictionary:
      globalDictionaryResponse.status === "fulfilled" ? globalDictionaryResponse.value : [],
    myDepartments:
      myDepartmentResponse.status === "fulfilled"
        ? (myDepartmentResponse.value.departments ?? [])
        : [],
    myDepartment:
      myDepartmentResponse.status === "fulfilled"
        ? myDepartmentResponse.value.department
        : null,
    isManager:
      myDepartmentResponse.status === "fulfilled"
        ? myDepartmentResponse.value.isManager
        : false,
  };
}

/**
 * PRODUCT_DEV-572: 自分の所属部署を取得（未所属/失敗は null = 絞り込みなし）
 */
async function fetchMyDepartment(
  tenantId: string,
): Promise<{
  department: MyDepartment;
  departments: MyDepartmentItem[];
  isManager: boolean;
}> {
  // 他の fetch と同様に tenantId をクエリで渡す。
  // authMiddleware は getTenantIdFromRequest でリクエストの tenantId を必要とするため、
  // 付け忘れると "Missing tenantId in request" で認証解決に失敗し常に null になる（PRODUCT_DEV-572 バグ）。
  const params = new URLSearchParams({ tenantId });
  const response = await apiGet<MyDepartmentResponse>(
    `${API_MY_DEPARTMENT}?${params.toString()}`,
  );
  return response.success
    ? {
        department: response.department,
        // 古いサーバは `departments` を返さない。単数側から1件の配列を作って揃える
        departments:
          response.departments ??
          (response.department ? [response.department] : []),
        isManager: response.isManager,
      }
    : { department: null, departments: [], isManager: false };
}

/**
 * プリセット一覧を取得
 */
async function fetchPresets(tenantId: string): Promise<DDBPreset[]> {
  const params = new URLSearchParams({ tenantId });
  const response = await apiGet<PresetsResponse>(
    `${API_PRESETS}?${params.toString()}`,
  );
  return response.success ? response.presets : [];
}

/**
 * 辞書マスター一覧を取得（有効なもののみ）
 */
async function fetchDictionaries(tenantId: string): Promise<DDBDictionary[]> {
  const params = new URLSearchParams({ tenantId });
  const response = await apiGet<DictionariesResponse>(
    `${API_DICTIONARIES}?${params.toString()}`,
  );
  return response.success ? response.dictionaries : [];
}

/**
 * リアルタイムAPI設定一覧を取得（アクティブなもののみ）
 */
async function fetchRealtimeConfigs(tenantId: string): Promise<DDBRealtimeConfig[]> {
  const params = new URLSearchParams({ tenantId });
  const response = await apiGet<RealtimeConfigsResponse>(
    `${API_REALTIME_CONFIGS}?${params.toString()}`,
  );
  return response.success ? response.realtimeConfigs : [];
}

/**
 * ONPプロンプト設定を取得（Admin管理テーブル）
 * AdminServiceStack-{env}-ONPPromptSettings テーブルから LAYER1/LAYER2 等を取得
 * 取得失敗時はundefinedを返し、ハードコードフォールバックに委譲
 */
async function fetchONPSettings(tenantId: string): Promise<ONPSettingsMap | undefined> {
  try {
    const params = new URLSearchParams({ tenantId });
    const response = await apiGet<{ settings: DDBONPPromptSetting[] }>(
      `${API_ONP_PROMPT_SETTINGS}?${params.toString()}`,
    );
    if (!response.settings || response.settings.length === 0) return undefined;
    const map: ONPSettingsMap = {};
    for (const s of response.settings) {
      map[s.settingId] = s;
    }
    return map;
  } catch {
    return undefined;
  }
}

/**
 * グローバル辞書を取得（Admin管理テーブル）
 * 取得失敗時は空配列を返す
 */
async function fetchGlobalDictionary(tenantId: string): Promise<DDBGlobalDictionaryEntry[]> {
  try {
    const params = new URLSearchParams({ tenantId });
    const response = await apiGet<{ entries: DDBGlobalDictionaryEntry[] }>(
      `${API_GLOBAL_DICTIONARY}?${params.toString()}`,
    );
    return response.entries || [];
  } catch {
    return [];
  }
}

// PRODUCT_DEV-652: fetchNotificationUnreadCount は削除した。
// 起動時に毎回 GET /notification/unread-count を叩いていたが、返り値 notificationUnreadCount を
// 読む画面が 1 つも無かった（未読バッジは useNewNotifications が独自ポーリングで算出している）。
